import React, { Fragment, memo, useCallback } from 'react';
import { Box, Button, Divider, List, ListSubheader, Typography } from '@mui/material';
import Styles from '../../FieldOverviewList/FieldOverview.module.less';
import { Close } from '@mui/icons-material';
import FieldOverviewListSection from '../../FieldOverviewList/FieldOverviewListSection';
import FieldOverviewListItem from '../../FieldOverviewList/FieldOverviewListItem';
import { useLangFile } from '../../../context/LanguageContext';
import { useFieldContext } from '../../../context/FieldContext';
import { getFieldName } from '../../../helpers/StateInterpreters';
import type Field from '../../../model/Field';

interface Section {
  id: string;
  header: string;
  cropType: string;
  fields: Field[];
}

interface BulkPrescriptionFieldOverviewProps {
  sections: Section[];
  onCloseClicked: () => void;
  onFieldClicked: (field: Field) => void;
  onFieldChecked: (field: Field, checked: boolean) => void;
  onCropTypeChecked: (cropType: string, checked: boolean) => void;
  onClearFilter: () => void;
  selectedField: Field | null;
  selectedFields: Field[];
  filteredCropTypes: string[];
}

const BulkPrescriptionFieldOverview: React.FC<BulkPrescriptionFieldOverviewProps> = ({
  sections,
  onCloseClicked,
  onFieldClicked,
  onFieldChecked,
  onCropTypeChecked,
  onClearFilter,
  selectedField,
  selectedFields,
  filteredCropTypes,
}) => {
  const { fields } = useFieldContext();
  const LangFile = useLangFile();

  const handleFieldClick = useCallback(
    (field: Field) => () => {
      onFieldClicked(field);
    },
    [onFieldClicked]
  );

  const handleFieldChecked = useCallback(
    (field: Field, checked: boolean) => () => {
      onFieldChecked(field, checked);
    },
    [onFieldChecked]
  );

  const handleSectionChecked = useCallback(
    (cropType: string, checked: boolean) => () => {
      onCropTypeChecked(cropType, checked);
    },
    [onCropTypeChecked]
  );

  return (
    <Fragment>
      <Box
        className={Styles.box}
        sx={{
          pb: 1,
        }}
      >
        <Box
          sx={{
            mx: 1.5,
            mt: 1.5,
            mb: 1,
            display: 'flex',
          }}
        >
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Button
                data-testid="bulk-field-list-close"
                className={Styles.button}
                onClick={onCloseClicked}
              >
                <Close />
              </Button>
              <Box
                sx={{
                  mx: 1.5,
                }}
              >
                <Typography>
                  {LangFile.FieldDrawer.title} ({fields.size})
                </Typography>
              </Box>
            </Box>
            <Button className={Styles.button} onClick={onClearFilter}>
              <Typography className={Styles.buttonText}>
                {LangFile.FieldIntegrationMapping.clearSelection}
              </Typography>
            </Button>
          </Box>
        </Box>
        <Divider />
        <List className={Styles.list} dense={true} subheader={<li />}>
          {sections.map((section) => (
            <li key={`section-${section.id}`}>
              <ul>
                <ListSubheader classes={{ root: Styles.subheader }} disableGutters={true}>
                  <FieldOverviewListSection
                    title={`${section.header} (${section.fields ? section.fields.length : 0})`}
                    checked={filteredCropTypes.includes(section.cropType)}
                    onCheckboxClick={(checked) => {
                      handleSectionChecked(section.cropType, checked)();
                    }}
                  />
                </ListSubheader>
                {section.fields.map((field) => (
                  <FieldOverviewListItem
                    id={field.fieldId}
                    onCheckboxClick={(checked) => {
                      handleFieldChecked(field, checked)();
                    }}
                    checked={selectedFields.some(
                      (selectedField) => selectedField.fieldId === field.fieldId
                    )}
                    onClick={handleFieldClick(field)}
                    showRadiobutton={false}
                    showCheckbox={true}
                    key={`item-${section.id}-${field.fieldId}`}
                    selected={selectedField && selectedField.fieldId === field.fieldId}
                    title={getFieldName(field, LangFile)}
                    subtitle={`${field.size.toFixed(1)} ha${field.cropVariation ? `, ${field.cropVariation}` : ''}`}
                  />
                ))}
              </ul>
            </li>
          ))}
        </List>
      </Box>
    </Fragment>
  );
};

export default memo(BulkPrescriptionFieldOverview);
